import { compareAsc, parseISO } from 'date-fns'
import { chatApiRequest } from '../client'
import {
  ChatConfig,
  ConversationsHistoryResponse,
  GetMessagesParams,
  MessagesResponse,
  PromptTemplatesResponse,
  SendMessagePayload,
  SendMessageResponse
} from '../../modules/chat/types/chat'
import { PaginatedOffsetParams } from '../../modules/chat/types/api'

type ConversationsHistoryParams = Partial<PaginatedOffsetParams> & {
  projectId: string
}

export const fetchChatConfig = async (): Promise<ChatConfig> => {
  const response = await chatApiRequest('/v1/config')

  if (!response.ok) {
    throw new Error('Не удалось загрузить конфигурацию чата')
  }

  return (await response.json()) as ChatConfig
}

export const fetchConversationsHistory = async ({
  projectId,
  limit,
  offset
}: ConversationsHistoryParams): Promise<ConversationsHistoryResponse> => {
  const searchParams = new URLSearchParams({ projectId })

  if (typeof limit === 'number') {
    searchParams.set('limit', String(limit))
  }
  if (typeof offset === 'number') {
    searchParams.set('offset', String(offset))
  }

  const response = await chatApiRequest(
    `/v1/conversations?${searchParams.toString()}`
  )

  if (!response.ok) {
    throw new Error('Не удалось загрузить историю диалогов')
  }

  return (await response.json()) as ConversationsHistoryResponse
}

export const deleteConversation = async (
  conversationId: string
): Promise<void> => {
  const response = await chatApiRequest(
    `/v1/conversations/${conversationId}`,
    {
      method: 'DELETE'
    }
  )

  if (!response.ok) {
    throw new Error('Не удалось удалить переписку')
  }
}

export const fetchPromptTemplates = async (
  projectId?: string
): Promise<PromptTemplatesResponse> => {
  const searchParams = new URLSearchParams()
  if (projectId) {
    searchParams.set('projectId', projectId)
  }
  const queryString = searchParams.size ? `?${searchParams.toString()}` : ''

  const response = await chatApiRequest(`/v1/prompts${queryString}`)

  if (!response.ok) {
    throw new Error('Не удалось загрузить промпт-шаблоны')
  }

  return (await response.json()) as PromptTemplatesResponse
}

export const fetchConversationMessages = async ({
  conversationId,
  limit,
  offset
}: GetMessagesParams): Promise<MessagesResponse> => {
  const searchParams = new URLSearchParams({ limit: String(limit) })

  searchParams.set('offset', String(offset))

  const queryString = searchParams.size ? `?${searchParams.toString()}` : ''

  const response = await chatApiRequest(
    `/v1/conversations/${conversationId}/messages${queryString}`
  )

  if (!response.ok) {
    throw new Error('Не удалось загрузить историю сообщений диалога')
  }

  const data = (await response.json()) as MessagesResponse
  const sortedMessages = [...data.messages].sort((a, b) =>
    compareAsc(parseISO(a.createdAt), parseISO(b.createdAt))
  )

  return {
    conversation: data.conversation,
    messages: sortedMessages,
    limit: data.limit ?? limit,
    offset: data.offset ?? offset,
    total: data.total
  }
}

export const sendMessage = async (
  payload: SendMessagePayload
): Promise<SendMessageResponse> => {
  const response = await chatApiRequest('/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    throw new Error('Не удалось отправить сообщение')
  }

  return (await response.json()) as SendMessageResponse
}
